import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2, EyeOff } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { UpdateBanner } from "@/components/UpdateBanner";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout, { ADMIN_PANEL_AREAS } from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import RecordsPage from "@/pages/RecordsPage";
import Conference from "@/pages/Conference";
import Reports from "@/pages/Reports";
import Admin from "@/pages/Admin";
import MissingReports from "@/pages/MissingReports";
import ActivityLog from "@/pages/ActivityLog";
import ForcePassword from "@/pages/ForcePassword";
import Messages from "@/pages/Messages";
import MyReceipts from "@/pages/MyReceipts";
import OtherCollections from "@/pages/OtherCollections";
import SpecialCollections from "@/pages/SpecialCollections";
import AdminReceipts from "@/components/admin/AdminReceipts";
import Expenses from "@/pages/Expenses";
import ResetPassword from "@/pages/ResetPassword";

const Loading = () => (
  <div className="flex min-h-[50vh] items-center justify-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
);

function Protected({ children }) {
  const { user, settings } = useApp();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePassword />;
  if (!settings) return <Loading />;
  return <Layout>{children}</Layout>;
}

function Guard({ feature, perm, admin, area, msg, orReset, children }) {
  const { isAdmin, featureOn, can, hasArea, user } = useApp();
  if (admin && !isAdmin) return <Navigate to="/" replace />;
  if (isAdmin && msg && !user?.can_message) return <Navigate to="/" replace />;
  if (isAdmin && area && !hasArea(...area) && !(orReset && user?.can_reset)) return <Navigate to="/" replace />;
  if (!isAdmin && ((feature && !featureOn(feature)) || (perm && !can(perm)))) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-600" data-testid="section-off">
        <EyeOff className="h-8 w-8" /> This section is turned off by the Master Admin.
      </div>
    );
  }
  return children;
}

function LoginRoute() {
  const { user } = useApp();
  if (user === null) return <Loading />;
  return user ? <Navigate to="/" replace /> : <Login />;
}

const REC = ["records", "reports"];
const page = (el, guard = {}) => <Protected><Guard {...guard}>{el}</Guard></Protected>;

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={page(<Dashboard />)} />
          <Route path="/tithes" element={page(<RecordsPage key="tithes" type="tithes" />, { feature: "tithes", area: REC })} />
          <Route path="/progress" element={page(<RecordsPage key="progress" type="progress" />, { feature: "progress", area: REC })} />
          <Route path="/donations" element={page(<RecordsPage key="donations" type="donations" />, { feature: "donations", area: REC })} />
          <Route path="/conference" element={page(<Conference />, { feature: "conference", area: ["conference"] })} />
          <Route path="/reports" element={page(<Reports />, { feature: "reports", perm: "church_can_view_reports", area: REC })} />
          <Route path="/expenses" element={page(<Expenses />, { admin: true, area: ["finance", "reports"] })} />
          <Route path="/other" element={page(<OtherCollections />, { admin: true, area: ["other"] })} />
          <Route path="/special" element={page(<SpecialCollections />, { area: ["special"] })} />
          <Route path="/receipt-book" element={page(<AdminReceipts />, { admin: true, area: ["receipts"] })} />
          <Route path="/messages" element={page(<Messages />, { feature: "messages", msg: true })} />
          <Route path="/my-receipts" element={page(<MyReceipts />)} />
          <Route path="/alerts" element={page(<MissingReports />, { admin: true, area: ["reminders"] })} />
          <Route path="/activity" element={page(<ActivityLog />, { admin: true, area: ["all"] })} />
          <Route path="/admin" element={page(<Admin />, { admin: true, area: ADMIN_PANEL_AREAS, orReset: true })} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
      <UpdateBanner />
    </AppProvider>
  );
}

export default App;
